<template>
  <div class="flex flex-col gap-4 w-full">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
      <div class="flex flex-col">
        <!-- the flow: charged, and what stays with you -->
        <div class="rounded-[20px] overflow-hidden border border-gray-200 bg-white">
          <div class="px-4 pt-4 pb-3.5">
            <div class="flex items-start justify-between gap-2">
              <div class="text-xs text-gray-500 font-medium pt-1 flex items-center gap-2">
                {{ hasPayMe ? 'נסלק בפועל דרך TimeIt Pay' : 'נסלק בפועל' }}
                <span
                  v-if="data.estimated"
                  class="text-[9.5px] font-bold bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 leading-none">
                  הערכה לפי מחירון
                </span>
              </div>
              <!-- period button (the design's in-card pill) -->
              <button
                type="button"
                class="flex items-center gap-1.5 bg-gray-100 rounded-full ps-3 pe-2.5 py-1.5 text-xs font-medium text-gray-700 shrink-0"
                @click="emit('changePeriod')"
              >
                <i class="pi pi-calendar text-[11px] text-gray-500"/>
                {{ periodLabel }}
                <i class="pi pi-chevron-down text-[9px] text-gray-400"/>
              </button>
            </div>
            <div class="text-[32px] font-bold leading-tight mt-0.5" style="letter-spacing: -0.5px">
              {{ formatCurrency(data.grossRevenue) }}
            </div>
            <div class="text-[11px] text-gray-500 mt-1">{{ data.transactionsCount }} עסקאות</div>
          </div>
          <div class="flex items-stretch bg-gray-50 border-t border-gray-100">
            <div class="flex-1 px-4 py-2.5">
              <div class="text-[10px] text-gray-500">החזרים ללקוחות</div>
              <div class="text-[13.5px] font-bold mt-0.5" style="color:#b91c1c">−{{ formatCurrency(data.totalRefunds) }}</div>
            </div>
            <div class="w-px bg-gray-200"/>
            <div class="px-4 py-2.5" style="flex: 1.4">
              <div class="text-[10px] text-gray-500">נשאר אצלך (נטו)</div>
              <div class="text-[13.5px] font-bold mt-0.5" style="color:#15803d">{{ formatCurrency(data.netRevenue) }}</div>
            </div>
          </div>
        </div>

        <!-- export reports -->
        <div
          v-wave
          class="card mt-3 flex items-center gap-3 cursor-pointer"
          @click="emit('exportReports')"
        >
          <span class="w-11 h-11 rounded-[13px] shrink-0 flex items-center justify-center" style="background:#edeafd">
            <i class="pi pi-download text-lg" style="color:#6831d0"/>
          </span>
          <span class="flex-1 min-w-0">
            <span class="block text-[14px] font-bold text-gray-900">ייצוא דוחות</span>
            <span class="block text-[11.5px] text-gray-500 mt-0.5">הכנסות · חשבוניות · תקבולים</span>
          </span>
          <i class="pi pi-chevron-left text-sm text-gray-400"/>
        </div>

        <!-- by payment method -->
        <h3 class="section-title">לפי אמצעי תשלום</h3>
        <div class="card !py-1">
          <template v-if="byMethod.length">
            <AnalyticsRankRow
              v-for="(m, i) of byMethod"
              :key="m.key"
              :name="m.label"
              :value="formatCurrency(m.value)"
              :sub="`${Math.round((m.value / totalByMethod) * 100)}%`"
              :pct="Math.round((m.value / byMethod[0].value) * 100)"
              :color="PALETTE[i % PALETTE.length]"
              :last="i === byMethod.length - 1"/>
          </template>
          <div v-else class="text-xs text-gray-400 py-6 text-center">אין תשלומים בתקופה שנבחרה</div>
        </div>
      </div>

      <div class="flex flex-col">
        <!-- what they paid you for -->
        <h3 class="section-title lg:!mt-0">על מה שילמו לך</h3>
        <div class="card !py-1">
          <template v-if="byWhat.length">
            <AnalyticsRankRow
              v-for="(w, i) of byWhat"
              :key="w.key"
              :name="w.label"
              :value="formatCurrency(w.value)"
              :sub="`${Math.round((w.value / totalByWhat) * 100)}%${w.count ? ` · ${w.count} עסקאות` : ''}`"
              :pct="Math.round((w.value / byWhat[0].value) * 100)"
              :color="MIX_COLORS[w.key] ?? '#64748b'"
              :last="i === byWhat.length - 1"/>
          </template>
          <div v-else class="text-xs text-gray-400 py-6 text-center">אין תשלומים בתקופה שנבחרה</div>
        </div>

        <!-- recent charges -->
        <div class="flex items-baseline justify-between mx-0.5 mb-2 mt-4">
          <h3 class="text-[13.5px] font-bold text-gray-900">חיובים אחרונים</h3>
          <span
            class="text-xs font-semibold text-primary cursor-pointer"
            @click="onAllTransactions">
            כל העסקאות
          </span>
        </div>
        <div class="card !py-0.5">
          <template v-if="recentCharges.length">
            <div
              v-for="(t, i) of recentCharges"
              :key="t._id"
              v-wave
              class="flex items-center gap-3 py-2.5 cursor-pointer"
              :class="{'border-b border-gray-100': i < recentCharges.length - 1}"
              @click="openTransaction(t)">
              <div class="w-8 h-8 rounded-full bg-gray-100 text-gray-600 shrink-0 flex items-center justify-center text-[11.5px] font-bold">
                {{ (t.customerName || 'לקוח').slice(0, 2) }}
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-[13px] font-semibold truncate">{{ t.customerName }}</div>
                <div class="text-[11px] text-gray-500 truncate mt-0.5">{{ t.description }}</div>
              </div>
              <div class="text-left shrink-0">
                <div class="text-[13px] font-bold">{{ formatCurrency(t.paidPrice ?? t.price) }}</div>
                <div class="text-[10px] text-gray-400 mt-0.5" dir="ltr">
                  {{ formatWhen(t) }} · {{ methodLabel(t.paymentMethod) }}<template v-if="t.refundedAmount"> · הוחזר</template>
                </div>
              </div>
            </div>
          </template>
          <div v-else class="text-xs text-gray-400 py-6 text-center">אין חיובים בתקופה שנבחרה</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed} from 'vue';
import {DateTime} from 'luxon';
import {
  IAnalyticsRevenueTab,
  IPaymentTransactionRow,
  IPaymentsResponse,
  PaymentMethod,
} from '@torim-app/types';
import {useDrawer} from '@torim-app/shared-components';

import PaymentDetailsDrawer from '@app/components/admin/pages/customer/PaymentDetailsDrawer.vue';
import AnalyticsRankRow from './AnalyticsRankRow.vue';
import {formatCurrency} from './analytics-charts';
import {trackEvent} from '@app/composables/posthog';

const props = defineProps<{
  data: IAnalyticsRevenueTab;
  periodLabel: string;
  /** recent charges with service descriptions (payments-page endpoint rows) */
  recentTransactions: IPaymentTransactionRow[];
  hasPayMe: boolean;
}>();

const emit = defineEmits<{
  allTransactions: [];
  changePeriod: [];
  exportReports: [];
  /** a refund went through — the parent should refresh the period totals */
  refunded: [];
}>();

const {openDrawer} = useDrawer();

const MIX_COLORS: Record<string, string> = {
  appointments: '#8864ee',
  bundles: '#f59e0b',
  vouchers: '#ec4899',
  products: '#14b8a6',
  advanced: '#3b82f6',
  other: '#64748b',
};

const PALETTE = ['#8864ee', '#14b8a6', '#f59e0b', '#ec4899', '#3b82f6', '#64748b'];

const byWhat = computed(() => props.data.revenueByReason.filter(r => r.value > 0));
const totalByWhat = computed(() => byWhat.value.reduce((s, r) => s + r.value, 0) || 1);
const byMethod = computed(() => props.data.revenueByPaymentMethod.filter(r => r.value > 0));
const totalByMethod = computed(() => byMethod.value.reduce((s, r) => s + r.value, 0) || 1);

const recentCharges = computed(() => props.recentTransactions.slice(0, 5));

const methodLabel = (m: PaymentMethod): string => {
  const map: Record<string, string> = {
    cash: 'מזומן',
    credit: 'אשראי',
    'bank-transfer': 'העברה בנקאית',
    bit: 'ביט',
    'pay-box': 'Paybox',
    'apple-pay': 'Apple Pay',
    'google-pay': 'Google Pay',
    'loyalty-program-discount': 'הטבת מועדון',
    'loyalty-program-points': 'נקודות מועדון',
  };
  return map[m] ?? m;
};

const formatWhen = (t: IPaymentTransactionRow): string => {
  const dt = DateTime.fromISO(t.date).setZone('Asia/Jerusalem');
  if (!dt.isValid) return '';
  return `${dt.toFormat('d.M')} · ${t.hour ?? dt.toFormat('HH:mm')}`;
};

const onAllTransactions = () => {
  trackEvent('analytics_payments_all_transactions_clicked');
  emit('allTransactions');
};

const openTransaction = (row: IPaymentTransactionRow) => {
  trackEvent('analytics_payments_transaction_opened');
  const selectedPayment = {
    _id: row._id,
    uuid: row.uuid,
    paymentReason: row.paymentReason,
    chargedDate: row.chargedDate,
    paidPrice: row.paidPrice,
    status: row.status,
    ezCountInvoiceLink: row.ezCountInvoiceLink,
    paymentMethod: row.paymentMethod,
    transactionNumber: row.transactionNumber,
    price: row.price,
    createdAt: row.createdAt,
    refundedAmount: row.refundedAmount,
    refunds: row.refunds,
  } as unknown as IPaymentsResponse;

  openDrawer(PaymentDetailsDrawer, {
    selectedPayment,
    onRefunded: (updated: IPaymentsResponse) => {
      const idx = props.recentTransactions.findIndex(t => t._id === row._id);
      if (idx !== -1) {
        props.recentTransactions[idx] = {
          ...props.recentTransactions[idx],
          status: updated.status,
          refundedAmount: updated.refundedAmount,
          refunds: updated.refunds,
        };
      }
      emit('refunded');
    },
  }, {title: 'פרטי תשלום'});
};
</script>

<style scoped lang="scss">
.section-title {
  font-size: 13.5px;
  font-weight: 700;
  color: rgb(24 24 27);
  margin: 18px 3px 9px;
}

.card {
  background: #fff;
  border: 1px solid rgb(228 228 231);
  border-radius: 18px;
  padding: 14px 15px;
}
</style>
